import React, { useCallback, useEffect, useState } from 'react'
import Breadcrumbs from 'components/Breadcrumbs'
import { Button, Grid, Icon, Typography } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import { ROUTES } from 'constants/routes'
import {
  DROP_DATA,
  FETCH_ORDERS,
  SET_FILTERS,
  SET_SORTING,
} from 'modules/AccountsMarketplace/Category/types'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/AccountsMarketplace/Category/filters'
import Table from 'modules/AccountsMarketplace/Category/subcomponents/Table'
import { Link } from 'react-router-dom'
import DropdownSortingMenu from 'components/DropdownSortingMenu'
import { Helmet } from 'react-helmet'
import { useGetCategory } from 'modules/AccountsMarketplace/hooks/useGetCategory'

const Category = () => {
  const { categoryId } = useParams()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const [page, setPage] = useState(1)

  const { filters, orders, pagination, loading, sorting } = useSelector(
    (state) => state['accounts-marketplace/category'],
  )

  const { category } = useGetCategory(categoryId)

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    setAction: SET_FILTERS,
    localStorageKey: 'accounts-marketplace/category',
  })

  useEffect(() => {
    filterManager.params &&
      dispatch({
        type: FETCH_ORDERS,
        payload: {
          params: {
            ...filterManager.params,
            offset: (page - 1) * pagination.limit,
            limit: pagination.limit,
            sort: sorting.prop,
            direction: sorting.direction,
            category_id: categoryId,
          },
        },
      })
  }, [filterManager.params, page, sorting])

  const setSorting = (newSorting) =>
    dispatch({ type: SET_SORTING, payload: newSorting })

  useEffect(() => {
    dispatch({ type: DROP_DATA })
    setPage(1)
  }, [filterManager.filters, sorting])

  const handleChangePage = useCallback(() => setPage(page + 1), [setPage, page])

  return (
    <>
      <Helmet defer={false}>
        <title>
          {`${category?.name ? `${category?.name} — ` : ''}${t(
            'accounts_marketplace::title',
          )}`}
        </title>
      </Helmet>
      <Grid gap={24} direction={'column'} className={'margin-bottom-64'}>
        <Breadcrumbs
          links={[
            {
              title: t('accounts_marketplace::title'),
              to: pathGen(ROUTES.ACCOUNTS_MARKETPLACE),
            },
            {
              title: category?.name,
            },
          ]}
          className={'margin-bottom-0'}
        />

        <Typography component={'h1'} variant={'Header-60'}>
          {category?.name}
        </Typography>

        <div>
          <Button
            component={Link}
            to={pathGen(ROUTES.ACCOUNTS_MARKETPLACE_CATEGORY_PRODUCTS, {
              categoryId: category?.id,
            })}
            size={'l'}
            startIcon={<Icon name={'plus'} />}
            variant={'primary'}
          >
            {t('accounts_marketplace::category.add_product')}
          </Button>
        </div>
      </Grid>

      <FilterPanel
        filterManager={filterManager}
        className={'margin-bottom-32'}
      />

      <Grid className={'margin-bottom-16'}>
        <DropdownSortingMenu
          sortingRuleMap={{
            order_created_at: {
              asc: 'order_created_at.asc',
              desc: 'order_created_at.desc',
            },
            product_name: {
              asc: 'product_name.asc',
              desc: 'product_name.desc',
            },
          }}
          i18nKey={'accounts_marketplace::category.sortmenu'}
          id={'advertisers-list-sort-menu'}
          sorting={sorting}
          onSortChange={setSorting}
        />
      </Grid>

      <Table
        onChangePage={handleChangePage}
        data={orders}
        pagination={pagination}
        loading={loading}
        setSorting={setSorting}
        category={category}
      />
    </>
  )
}

export default Category
